'use client';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Circle, RotateCcw, MapPin } from 'lucide-react';
import type { School } from '@/data/schools';
import { khdaBannerColor } from '@/data/schools';
import { COMMUNITY_PRICES } from '@/data/communityPrices';
import KhdaBadge from '@/components/ui/KhdaBadge';
import CurriculumBadge from '@/components/ui/CurriculumBadge';
import Link from 'next/link';

interface MapViewProps {
  schools: School[];
  communityFilter: string;
  selectedSchoolId?: string | null;
  onSelectSchool?: (id: string | null) => void;
}

// Haversine distance in km
function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const RATING_LEGEND = [
  'Outstanding',
  'Very Good',
  'Good',
  'Acceptable',
  'Weak',
  'Very Weak',
] as const;

export default function MapView({ schools, communityFilter, selectedSchoolId, onSelectSchool }: MapViewProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const leafletMapRef = useRef<import('leaflet').Map | null>(null);
  const markersRef = useRef<import('leaflet').Marker[]>([]);
  const communityMarkersRef = useRef<import('leaflet').Marker[]>([]);
  const radiusCircleRef = useRef<import('leaflet').Circle | null>(null);
  const radiusCenterMarkerRef = useRef<import('leaflet').Marker | null>(null);

  const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
  const [radiusCenter, setRadiusCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [radiusKm, setRadiusKm] = useState(10);
  const [drawMode, setDrawMode] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [showCommunities, setShowCommunities] = useState(true);

  const schoolsInRadius = radiusCenter
    ? schools.filter((s) => haversineKm(s.coordinates.lat, s.coordinates.lng, radiusCenter.lat, radiusCenter.lng) <= radiusKm)
    : schools;

  const displaySchools = radiusCenter ? schoolsInRadius : schools;

  // Initialize Leaflet map (client-side only)
  useEffect(() => {
    if (!mapRef.current || leafletMapRef.current) return;

    let isMounted = true;

    import('leaflet').then((L) => {
      if (!isMounted || !mapRef.current || leafletMapRef.current) return;

      // Fix default icon paths for Next.js
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (L.Icon.Default.prototype as any)._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      });

      const map = L.map(mapRef.current!, {
        center: [25.2048, 55.2708],
        zoom: 11,
        zoomControl: true,
      });

      L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
        attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © <a href="https://carto.com/attributions">CARTO</a>',
        maxZoom: 19,
        subdomains: 'abcd',
      }).addTo(map);

      leafletMapRef.current = map;
      setMapReady(true);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  // Cleanup map on unmount
  useEffect(() => {
    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
    };
  }, []);

  // Handle draw mode
  useEffect(() => {
    const map = leafletMapRef.current;
    if (!map || !mapReady) return;

    if (!drawMode) {
      map.getContainer().style.cursor = '';
      return;
    }

    map.getContainer().style.cursor = 'crosshair';
    map.dragging.disable();

    const handleClick = (e: import('leaflet').LeafletMouseEvent) => {
      setRadiusCenter({ lat: e.latlng.lat, lng: e.latlng.lng });
      setDrawMode(false);
      map.getContainer().style.cursor = '';
      map.dragging.enable();
    };

    map.once('click', handleClick);

    return () => {
      map.off('click', handleClick);
      map.getContainer().style.cursor = '';
      map.dragging.enable();
    };
  }, [drawMode, mapReady]);

  // Update radius circle
  useEffect(() => {
    const map = leafletMapRef.current;
    if (!map || !mapReady) return;

    import('leaflet').then((L) => {
      if (radiusCircleRef.current) {
        radiusCircleRef.current.remove();
        radiusCircleRef.current = null;
      }
      if (radiusCenterMarkerRef.current) {
        radiusCenterMarkerRef.current.remove();
        radiusCenterMarkerRef.current = null;
      }

      if (radiusCenter) {
        const circle = L.circle([radiusCenter.lat, radiusCenter.lng], {
          radius: radiusKm * 1000,
          color: '#0ea5e9',
          fillColor: '#0ea5e9',
          fillOpacity: 0.08,
          weight: 2,
          dashArray: '6 4',
        }).addTo(map);
        radiusCircleRef.current = circle;

        const centerIcon = L.divIcon({
          className: '',
          html: `<div style="width:12px;height:12px;background:#0ea5e9;border:2px solid white;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.3);"></div>`,
          iconSize: [12, 12],
          iconAnchor: [6, 6],
        });
        const centerMarker = L.marker([radiusCenter.lat, radiusCenter.lng], { icon: centerIcon }).addTo(map);
        radiusCenterMarkerRef.current = centerMarker;
      }
    });
  }, [radiusCenter, radiusKm, mapReady]);

  // Update school markers with hover tooltip showing name + KHDA rating
  useEffect(() => {
    const map = leafletMapRef.current;
    if (!map || !mapReady) return;

    import('leaflet').then((L) => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];

      displaySchools.forEach((school) => {
        const color = khdaBannerColor(school.khdaRating);
        const isSelected = selectedSchoolId === school.id;
        const icon = L.divIcon({
          className: '',
          html: `<div style="
            width: ${isSelected ? '36px' : '28px'};
            height: ${isSelected ? '46px' : '36px'};
            position: relative;
            cursor: pointer;
            transition: all 0.15s;
          ">
            <svg viewBox="0 0 28 36" width="${isSelected ? '36' : '28'}" height="${isSelected ? '46' : '36'}" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 0C6.268 0 0 6.268 0 14c0 9.333 14 22 14 22S28 23.333 28 14C28 6.268 21.732 0 14 0z" fill="${color}" stroke="${isSelected ? 'white' : 'white'}" stroke-width="${isSelected ? '2.5' : '1.5'}"/>
              <circle cx="14" cy="14" r="${isSelected ? '7' : '6'}" fill="white"/>
            </svg>
            ${isSelected ? `<div style="position:absolute;top:-6px;left:50%;transform:translateX(-50%);width:8px;height:8px;background:${color};border-radius:50%;border:2px solid white;"></div>` : ''}
          </div>`,
          iconSize: [isSelected ? 36 : 28, isSelected ? 46 : 36],
          iconAnchor: [isSelected ? 18 : 14, isSelected ? 46 : 36],
          popupAnchor: [0, isSelected ? -46 : -36],
        });

        const tooltipHtml = `
          <div style="font-family:sans-serif;padding:2px 0;">
            <div style="font-weight:600;font-size:12px;line-height:1.3;margin-bottom:3px;">${school.name}</div>
            <div style="display:inline-block;background:${color};color:white;padding:1px 7px;border-radius:4px;font-size:10px;font-weight:600;">KHDA: ${school.khdaRating}</div>
          </div>`;

        const marker = L.marker([school.coordinates.lat, school.coordinates.lng], { icon })
          .addTo(map)
          .bindTooltip(tooltipHtml, {
            direction: 'top',
            offset: [0, isSelected ? -46 : -36],
            opacity: 1,
            className: 'leaflet-school-tooltip',
          })
          .bindPopup(
            `<div style="min-width:180px;font-family:sans-serif;">
              <div style="font-weight:600;font-size:13px;margin-bottom:4px;line-height:1.3;">${school.name}</div>
              <div style="font-size:11px;color:#64748b;margin-bottom:6px;">${school.community}</div>
              <div style="font-size:11px;margin-bottom:4px;">
                <span style="background:${color};color:white;padding:1px 6px;border-radius:4px;font-size:10px;font-weight:600;">${school.khdaRating}</span>
                &nbsp;<span style="color:#475569;">${school.curriculum}</span>
              </div>
              <div style="font-size:11px;color:#475569;">Age: ${school.ageRange}</div>
              <div style="font-size:11px;color:#475569;">Fees: AED ${school.feeRangeMin.toLocaleString()}–${school.feeRangeMax.toLocaleString()}</div>
            </div>`,
            { maxWidth: 240 }
          );

        marker.on('click', () => {
          setSelectedSchool(school);
          if (onSelectSchool) onSelectSchool(school.id);
        });

        markersRef.current.push(marker);
      });
    });
  }, [displaySchools, mapReady, selectedSchoolId, onSelectSchool]);

  // Add community name labels on map
  useEffect(() => {
    const map = leafletMapRef.current;
    if (!map || !mapReady) return;

    import('leaflet').then((L) => {
      // Remove existing community markers
      communityMarkersRef.current.forEach((m) => m.remove());
      communityMarkersRef.current = [];

      if (!showCommunities) return;

      COMMUNITY_PRICES.forEach((community) => {
        const labelIcon = L.divIcon({
          className: '',
          html: `<div style="
            background: rgba(255,255,255,0.92);
            border: 1px solid #cbd5e1;
            border-radius: 4px;
            padding: 2px 6px;
            font-family: sans-serif;
            font-size: 10px;
            font-weight: 600;
            color: #334155;
            white-space: nowrap;
            box-shadow: 0 1px 3px rgba(0,0,0,0.15);
            pointer-events: none;
          ">${community.name}</div>`,
          iconSize: undefined,
          iconAnchor: [0, 0],
        });

        const marker = L.marker([community.lat, community.lng], {
          icon: labelIcon,
          interactive: false,
          zIndexOffset: -100,
        }).addTo(map);

        communityMarkersRef.current.push(marker);
      });
    });
  }, [mapReady, showCommunities]);

  const handleClearRadius = useCallback(() => {
    setRadiusCenter(null);
  }, []);

  return (
    <div className="flex flex-col h-full">
      {/* Map Controls */}
      <div className="flex items-center gap-3 p-3 bg-white border-b border-border flex-wrap">
        <button
          onClick={() => setDrawMode(!drawMode)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all duration-150 ${
            drawMode
              ? 'bg-primary text-white border-primary' : 'bg-white text-foreground border-border hover:border-primary/40'
          }`}
        >
          <Circle size={15} />
          {drawMode ? 'Click map to set radius' : 'Draw Radius'}
        </button>

        {/* Toggle community labels */}
        <button
          onClick={() => setShowCommunities(!showCommunities)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all duration-150 ${
            showCommunities
              ? 'bg-sky-50 text-sky-700 border-sky-200' : 'bg-white text-foreground border-border hover:border-primary/40'
          }`}
        >
          <MapPin size={15} />
          {showCommunities ? 'Hide Communities' : 'Show Communities'}
        </button>

        {radiusCenter && (
          <>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Radius:</span>
              <input
                type="range"
                min={2}
                max={30}
                step={1}
                value={radiusKm}
                onChange={(e) => setRadiusKm(Number(e.target.value))}
                className="w-28 accent-primary"
              />
              <span className="text-sm font-semibold text-foreground tabular-nums">{radiusKm} km</span>
            </div>
            <button
              onClick={handleClearRadius}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground border border-border hover:border-foreground/20 transition-all"
            >
              <RotateCcw size={13} />
              Clear
            </button>
            <span className="text-sm text-primary font-medium">{schoolsInRadius.length} schools in radius</span>
          </>
        )}

        {/* Legend — matches banner colours */}
        <div className="ml-auto flex items-center gap-3 flex-wrap">
          {RATING_LEGEND.map((r) => (
            <div key={`legend-${r}`} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: khdaBannerColor(r as Parameters<typeof khdaBannerColor>[0]) }}
              />
              {r}
            </div>
          ))}
        </div>
      </div>

      {/* Map + Panel */}
      <div className="flex flex-1 overflow-hidden">
        {/* Leaflet Map Container */}
        <div className="flex-1 relative" style={{ minHeight: 460 }}>
          <div
            ref={mapRef}
            className="absolute inset-0"
            style={{ zIndex: 0 }}
          />
          {!mapReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-100 z-10">
              <div className="text-sm text-muted-foreground">Loading map…</div>
            </div>
          )}
          {drawMode && mapReady && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 pointer-events-none z-20">
              <div className="bg-primary text-white rounded-xl px-5 py-3 shadow-lg text-sm font-medium flex items-center gap-2">
                <Circle size={16} />
                Click anywhere on the map to set your radius centre
              </div>
            </div>
          )}
          <div className="absolute top-3 left-3 pointer-events-none z-10">
            <div className="bg-white/90 backdrop-blur-sm border border-border rounded-lg px-3 py-2 shadow-sm">
              <p className="text-xs font-semibold text-foreground">Dubai School Map</p>
              <p className="text-xs text-muted-foreground">{displaySchools.length} schools shown</p>
            </div>
          </div>
        </div>

        {/* School Detail Panel */}
        {selectedSchool && (
          <div className="w-72 bg-white border-l border-border overflow-y-auto scrollbar-thin animate-slide-in-right" style={{ zIndex: 1 }}>
            <div className="p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-display font-semibold text-sm text-foreground leading-tight">{selectedSchool.name}</h4>
                  <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                    <MapPin size={10} />
                    {selectedSchool.community}
                  </p>
                </div>
                <button onClick={() => { setSelectedSchool(null); if (onSelectSchool) onSelectSchool(null); }} className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground">
                  <span className="text-lg leading-none">×</span>
                </button>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <KhdaBadge rating={selectedSchool.khdaRating} size="sm" />
                <CurriculumBadge curriculum={selectedSchool.curriculum} size="sm" />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="bg-muted/50 rounded-lg p-2.5">
                  <p className="text-xs text-muted-foreground">Age Range</p>
                  <p className="text-sm font-semibold">{selectedSchool.ageRange}</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-2.5">
                  <p className="text-xs text-muted-foreground">Min Fee</p>
                  <p className="text-sm font-semibold tabular-nums">AED {selectedSchool.feeRangeMin.toLocaleString()}</p>
                </div>
              </div>

              <Link
                href={`/school-profile-comparison?school=${selectedSchool.id}`}
                className="block w-full text-center py-2.5 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90 transition-all active:scale-95"
              >
                View Full Profile
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}